/**
 * ThrottleButton - 버튼 쓰로틀링 유틸리티
 * 
 * @example
 * const throttle = new ThrottleButton({
 *   buttonSelector: '#ResendButton',
 *   formSelector: '#RecoverPasswordForm',
 *   duration: 60000,
 *   storageKey: 'my_throttle_key',
 *   onTick: (seconds) => console.log(`${seconds}초 남음`),
 *   onEnabled: () => console.log('버튼 활성화'),
 *   onDisabled: () => console.log('버튼 비활성화')
 * });
 */
class ThrottleButton {
  /**
   * @param {Object} options - 설정 옵션
   * @param {string} options.buttonSelector - 버튼 선택자 (필수)
   * @param {string} [options.formSelector] - 폼 선택자 (없으면 버튼의 부모 폼 사용)
   * @param {number} [options.duration=60000] - 쓰로틀링 시간 (ms)
   * @param {string} [options.storageKey] - localStorage 키 (기본값 자동 생성)
   * @param {Function} [options.onTick] - 매초 호출되는 콜백 (remainingSeconds)
   * @param {Function} [options.onEnabled] - 버튼 활성화 시 콜백
   * @param {Function} [options.onDisabled] - 버튼 비활성화 시 콜백
   */
  constructor(options) {
    this.options = {
      duration: 60000,
      storageKey: `throttle_${options.buttonSelector.replace(/[^a-zA-Z0-9]/g, '_')}`,
      onTick: null,
      onEnabled: null,
      onDisabled: null,
      ...options
    };

    this.button = document.querySelector(this.options.buttonSelector);
    if (!this.button) {
      console.warn(`ThrottleButton: Button not found (${this.options.buttonSelector})`);
      return;
    }

    this.form = this.options.formSelector 
      ? document.querySelector(this.options.formSelector)
      : this.button.closest('form');

    this.timerId = null;
    this.init();
  }

  /**
   * 초기화
   */
  init() {
    this.bindEvents();
    this.startCountdown();
  }

  /**
   * 이벤트 바인딩
   */
  bindEvents() {
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }
  }

  /**
   * 폼 제출 핸들러
   * @param {Event} e - 이벤트 객체
   */
  handleSubmit(e) {
    const remaining = this.getTimeRemaining();
    if (remaining > 0) {
      e.preventDefault();
      return false;
    }
    this.saveTimestamp();
  }

  /**
   * 타임스탬프 저장
   */
  saveTimestamp() {
    localStorage.setItem(this.options.storageKey, Date.now().toString());
  }

  /**
   * 남은 시간 계산 (ms)
   * @returns {number} 남은 시간 (밀리초)
   */
  getTimeRemaining() {
    const lastSent = localStorage.getItem(this.options.storageKey);
    if (!lastSent) return 0;
    const elapsed = Date.now() - parseInt(lastSent, 10);
    return Math.max(0, this.options.duration - elapsed);
  }

  /**
   * UI 업데이트
   * @param {number} remaining - 남은 시간 (ms)
   */
  updateUI(remaining) {
    const seconds = Math.ceil(remaining / 1000);
    
    if (remaining > 0) {
      this.disableButton();
      if (typeof this.options.onTick === 'function') {
        this.options.onTick(seconds);
      }
      if (typeof this.options.onDisabled === 'function') {
        this.options.onDisabled(seconds);
      }
    } else {
      this.enableButton();
      if (typeof this.options.onEnabled === 'function') {
        this.options.onEnabled();
      }
    }
  }

  /**
   * 버튼 비활성화
   */
  disableButton() {
    this.button.disabled = true;
    this.button.setAttribute('aria-disabled', 'true');
  }

  /**
   * 버튼 활성화
   */
  enableButton() {
    this.button.disabled = false;
    this.button.removeAttribute('aria-disabled');
  }

  /**
   * 카운트다운 시작
   */
  startCountdown() {
    this.clearTimer();
    
    const tick = () => {
      const remaining = this.getTimeRemaining();
      this.updateUI(remaining);
      
      if (remaining > 0) {
        this.timerId = setTimeout(tick, 1000);
      }
    };
    
    tick();
  }

  /**
   * 타이머 정리
   */
  clearTimer() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  /**
   * 쓰로틀링 리셋 (테스트/디버깅용)
   */
  reset() {
    localStorage.removeItem(this.options.storageKey);
    this.clearTimer();
    this.enableButton();
    if (typeof this.options.onEnabled === 'function') {
      this.options.onEnabled();
    }
  }

  /**
   * 인스턴스 정리
   */
  destroy() {
    this.clearTimer();
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit.bind(this));
    }
  }
}

// 전역으로 노출
if (typeof window !== 'undefined') {
  window.ThrottleButton = ThrottleButton;
}
